import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { Box, BoxProps, Button, ButtonProps, Typography, TypographyProps } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import gsap from 'gsap';
import GenericPageContainer from '../GenericPageContainer/GenericPageContainer';
import { ControlsStringAssets, MessagesStringAssets, PageAndViewTitleStringAssets } from '../../../assets/stringAssets';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/styles';
import { useFirebaseAuth } from '../../customHooks';
import { IFirebaseAuthAndUserAuthenticationStatus } from '../../customHooks/hookReturnObjects';
import MyDigiBrainLogo from '../../images/MyDigiBrainLogo/MyDigiBrainLogo';
import Aggregation from '../../images/Aggregation/Aggregation';
import Collaboration from '../../images/Collaboration/Collaboration';
import Consolidation from '../../images/Consolidation/Consolidation';
import Navigation from '../../images/Navigation/Navigation';
import { IRegistrationData } from '../../../dataObjects/models/registerAndLogin/RegistrationData';
import { alertInfoChange } from '../../../uiMiddleware-redux/slices/alertInfo/alertInfoSlice';
import { IStoreState } from '../../../uiMiddleware-redux/store';
import { composeMessageUsingStringAsset } from '../../messages';
import { AlertInfo } from '../../../dataObjects/models/alerts/AlertInfo';
import { enumAlertType } from '../../enums';
import { useAppDispatch, useAppSelector } from '../../../uiMiddleware-redux/store/configureStore';
import { EducationalVideosStringAssets } from '../../../assets/stringAssets/EducationalVideosStringAssets';
import fileBasedEducationalVideosCatalog_Json from '../../../assets/data/educationalVideosCatalog.json';
import { EducationalVideoItem, IEducationalVideoItem } from '../../../dataObjects/models/educationalVideos';
import { JsonConverter } from '../../../dataObjects/utilities/JsonConverter';
import { EducationalVideosVideoGridViewItem } from '../../views/educationalVideos/EducationalVideosVideoGridViewItem';
import { getEducationalVideoItemFromVideoId } from '../../../dataObjects/utilities/educationalVideos';


// the following style object represents Css specs for all of the Details Section Graphics objects
const animatedLogoGraphicStyle: {} = {
  position: 'absolute',
  height: '100%',  // 100% of its parent element
  width: '100%',  // 100% of its parent element
};

// the following style object represents Css specs for all of the Details Section Graphics objects
const detailsSectionGraphicStyle: {} = {
  position: 'absolute',
  height: '100%',  // 100% of its parent element
};

const useStyles: () => Record<string, string> = makeStyles((theme: Theme) => ({

  myDigiBrainLogo: {
    margin: theme.spacing(0, 0, 2, 0),

    [theme.breakpoints.up('xs')]: {
      height: '80px',
    },
    [theme.breakpoints.up('sm')]: {
      height: '90px',
    },
    [theme.breakpoints.up('md')]: {
      height: '100px',
    },
    [theme.breakpoints.up('lg')]: {
      height: '110px',
    },
    [theme.breakpoints.up('xl')]: {
      height: '120px',
    },

  },
}));


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area to display the overall header
const StyledBoxForPaddingAtopPage = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// a styled Grid for containing the Login Button
const StyledGridForLoginButtonContainer = styled((props) => (
  <Grid
    container
    {...props}
  />
))(({ theme }) => ({
  marginRight: theme.spacing(0.5),
}));

// a styled Grid for containing the Create New Account Button
const StyledGridForCreateAccountButtonContainer = styled((props) => (
  <Grid
    container
    {...props}
  />
))(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
}));

// a styled Button for the Sign-in & Register Buttons
const StyledButtonForLoginAndRegisterButtons = styled((props: ButtonProps) => (
  <Button
    fullWidth
    {...props}
  />
))(({ theme }) => ({
  padding: theme.spacing(1, 2),
  margin: theme.spacing(1),
  color: theme.secondaryButton.textColor,
  border: theme.secondaryButton.border,
  background: theme.secondaryButton.background,
  '&:hover': {
    border: theme.secondaryButton.hoverBorder,
    background: theme.secondaryButton.hoverBackground,
    color: theme.secondaryButton.hoverTextColor,
  },
  '&:disabled': {
    border: theme.secondaryButton.disabledBorder,
    background: theme.secondaryButton.disabledBackground,
    color: theme.secondaryButton.disabledTextColor,
  },
}));

// a styled Typography for information text content
const StyledTypographyForInformationText = styled((props) => (
  <Typography
    variant='body1'
    component='div'
    {...props}
  />
))(({ theme }) => ({
  color: theme.infoText.color,
  marginTop: theme.spacing(2),
}));

// a styled Typography for a details section content
const StyledTypographyForDetailsSectionGeneralContent = styled((props) => (
  <Typography
    variant='body1'
    component='span'
    {...props}
  />
))(({ theme }) => ({
  color: theme.infoText.color,
  margin: theme.spacing(0.5, 0, 0, 0),
}));

// a styled Grid for displaying the primary educational video
const StyledGridForPrimaryVideoDisplay = styled((props) => (
  <Grid
    container
    size={{ xs: 12, sm: 8, md: 6 }}
    alignItem={'center'}
    alignSelf={'center'}
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(1),

  // [theme.breakpoints.up('xs')]: {
  //   marginBottom: theme.spacing(2),
  // },
}));

// a styled Grid for displaying side-by-side educational videos
const StyledGridForSideBySideVideosDisplay = styled((props) => (
  <Grid
    container
    size={{ xs: 12, sm: 8, md: 6 }}
    alignItem={'center'}
    alignSelf={'flex-start'}
    justifyItem={'center'}
    justifySelf={'center'}
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),

  // [theme.breakpoints.up('xs')]: {
  //   marginBottom: theme.spacing(2),
  // },
}));

// a styled Grid for overall details section
const StyledGridForDetailsSection = styled((props) => (
  <Grid
    container
    {...props}
  />
))(({ theme }) => ({
  margin: theme.spacing(2, 0, 2, 0),
}));

// a styled Grid for a details block
const StyledGridForDetailsBlock = styled((props) => (
  <Grid
    container
    size={{ xs: 12, lg: 6 }}
    // justifyItems={'flex-start'}
    justifyItems={'stretch'}
    alignItem={'center'}
    {...props}
  />
))(({ theme }) => ({
  marginBottom: theme.spacing(1),

  [theme.breakpoints.up('xs')]: {
    marginBottom: theme.spacing(2),
  },
}));

// a styled Grid for the details graphics area
const StyledGridForDetailsGraphicsArea = styled((props) => (
  <Grid
    container
    justifyItems={'stretch'}
    alignItem={'center'}
    {...props}
  />
))(({ theme }) => ({
}));

// a styled Box (equivalent to a <section> here), providing a bounding box for the MyDigiBrain Logo
const StyledBoxLogoBoundingBox = styled((props: BoxProps) => (
  <Box
    justifyItems='center'
    alignItems='center'
    {...props}
  />
))(({ theme }) => ({
  // border: theme.aboutPageInfoGraphics.boundingBoxBorder,
  // borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(2, 2, 2, 0),
  // background: theme.aboutPageInfoGraphics.boundingBoxBackground,
  // width: '100%',
  position: 'relative',
  transition: '500ms ease-in',

  [theme.breakpoints.up('xs')]: {
    height: '120px',
    width: '120px',
  },
  [theme.breakpoints.up('sm')]: {
    height: '135px',
    width: '135px',
  },
  [theme.breakpoints.up('md')]: {
    height: '150px',
    width: '150px',
  },
  [theme.breakpoints.up('lg')]: {
    height: '165px',
    width: '165px',
  },
  [theme.breakpoints.up('xl')]: {
    height: '180px',
    width: '180px',
  },
}));

// a styled Box (equivalent to a <section> here), providing a bounding box for graphics in the features area
const StyledBoxForFeatureGraphicsBoundingBox = styled((props: BoxProps) => (
  <Box
    justifyItems='center'
    alignItems='center'
    {...props}
  />
))(({ theme }) => ({
  // border: theme.aboutPageInfoGraphics.boundingBoxBorder,
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(2, 2, 2, 0),
  background: theme.aboutPageInfoGraphics.boundingBoxBackground,
  width: '100%',
  position: 'relative',
  transition: '500ms ease-in',

  [theme.breakpoints.up('xs')]: {
    height: '100px',
    width: '100px',
  },
  [theme.breakpoints.up('sm')]: {
    height: '110px',
    width: '110px',
  },
  [theme.breakpoints.up('md')]: {
    height: '120px',
    width: '120px',
  },
  [theme.breakpoints.up('lg')]: {
    height: '140px',
    width: '140px',
  },
  [theme.breakpoints.up('xl')]: {
    height: '150px',
    width: '150px',
  },
}));

// a styled SVG for Digital Media
const StyledSvgForMyDigiBrainLogo = styled((props) => (
  <MyDigiBrainLogo
    {...props}
  />
))(({ theme }) => (
  animatedLogoGraphicStyle
));

// a styled SVG for Aggregation Icon
const StyledSvgForAggregation = styled((props) => (
  <Aggregation
    {...props}
  />
))(({ theme }) => (
  detailsSectionGraphicStyle
  // {
  //   scale: '80%'
  // }
));

// a styled SVG for Consolidation Icon
const StyledSvgForConsolidation = styled((props) => (
  <Consolidation
    {...props}
  />
))(({ theme }) => (
  detailsSectionGraphicStyle
  // {
  //   scale: '80%'
  // }
));

// a styled SVG for Collaboration Icon
const StyledSvgForCollaboration = styled((props) => (
  <Collaboration
    {...props}
  />
))(({ theme }) => (
  detailsSectionGraphicStyle
  // {
  //   scale: '80%'
  // }
));

// a styled SVG for Navigation Icon
const StyledSvgForNavigation = styled((props) => (
  <Navigation
    {...props}
  />
))(({ theme }) => (
  detailsSectionGraphicStyle
  // {
  //   scale: '80%'
  // }
));

export interface IAboutPageProps extends PropsWithChildren<unknown> {

}

const AboutPage: React.FC<IAboutPageProps> = (props: IAboutPageProps) => {
  console.info('Entering AboutPage...');

  AboutPage.displayName = 'About Page';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  const classes: Record<string, string> = useStyles();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const logoElement: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);


  const performLogoEntrance: () => void = useCallback((): void => {

    // The starting point for the animation will be to:
    //  - Make the logo invisible (opacity: 0);
    //  - Set its original orientation to 0 (rotate: 0), and 
    //  - Make the logo 10% of its size (scale: 0.10)
    gsap.from(logoElement.current, {
      opacity: 0,
      rotate: 0,
      scale: 0.10,
    });

    // Make the logo fully visible (opacity: 1.0)
    gsap.to(logoElement.current, {
      // scale: 1.0,
      opacity: 1.0,
      duration: 1,
    });

    // Bring the logo to its full size (scale: 1.0)
    gsap.to(logoElement.current, {
      scale: 1.0,
      // opacity: 1.0,
      // rotation: 720,
      duration: 3,
    });

  }, []);

  useEffect(() => {
    performLogoEntrance();
  }, [performLogoEntrance]);

  // User Registration Data, for the case where a user has just registered (created a new account)
  const [registrationData, setRegistrationData] = useState<IRegistrationData | undefined>(undefined);

  // whether user is authenticated
  const [userAuthenticated, setUserAuthenticated] = useState(false);

  /**
   * @function clearAlertInfoLocal Clears Alert information from Redux state
   */
  const clearAlertInfoLocal: () => void = useCallback((): void => {
    dispatch(alertInfoChange(null));
  }, [dispatch]);

  // Check Redux state to see if a user just registered and has been redirected to the Login page.
  const userRegistrationData: IRegistrationData | null | undefined = useAppSelector((state: IStoreState) => state.userRegistrationData);
  if (userRegistrationData && !registrationData) {
    setRegistrationData(userRegistrationData);
  }

  useEffect(() => {
    if (registrationData) {
      const newRegistrationMessage: string = composeMessageUsingStringAsset(MessagesStringAssets.registration_Success, registrationData.email, MessagesStringAssets.substitutionKeyword);
      dispatch(alertInfoChange(new AlertInfo(true, enumAlertType.Success, newRegistrationMessage)));
    }

    // cleanup upon unmounting the componenent
    return () => {
      clearAlertInfoLocal();
    }
  }, [clearAlertInfoLocal, dispatch, registrationData])


  // the video item to display on this page
  const [videoItemToDisplay, setVideoItemToDisplay] = useState<IEducationalVideoItem | undefined>(undefined);

  const [overviewVideoItem, setOverviewVideoItem] = useState<IEducationalVideoItem | undefined>(undefined);
  const [creatingAUserAccountVideoItem, setCreatingAUserAccountVideoItem] = useState<IEducationalVideoItem | undefined>(undefined);


  // a useEffect hook that is executed upon mounting of the component
  useEffect(() => {
    displayConsoleLogs && console.log(`%c AboutPage, in useEffect upon mounting. Ready to read the Product Videos Catalog from a file.`, 'background: #600; color: #fff');

    // // get the entire video catalog
    // const educationalVideosCatalog_fromJsonFile: Array<IEducationalVideoItem> = JsonConverter.arrayFromJSONArray(EducationalVideoItem, fileBasedEducationalVideosCatalog_Json);

    // // from the video catalog, get the video item we're going display on this page
    // const videoItem: IEducationalVideoItem | undefined = educationalVideosCatalog_fromJsonFile.find(productVideoItem => productVideoItem.id === EducationalVideosStringAssets.myDigiBrainOverviewVideoId);

    // from the video catalog, get the video item we're going display on this page
    const videoItem: IEducationalVideoItem | undefined = getEducationalVideoItemFromVideoId(EducationalVideosStringAssets.myDigiBrainOverviewVideoId);

    setVideoItemToDisplay(videoItem);

    // get the video items for the "Intro & Overview" video and "Creating a User Account" video
    setOverviewVideoItem(getEducationalVideoItemFromVideoId(EducationalVideosStringAssets.myDigiBrainOverviewVideoId));
    setCreatingAUserAccountVideoItem(getEducationalVideoItemFromVideoId(EducationalVideosStringAssets.createAUserAccountId));

  }, []);



  // get the Firebase auth state using a custom hook
  const { auth, userAuthenticated: userIsAuthenticated }: IFirebaseAuthAndUserAuthenticationStatus = useFirebaseAuth();

  // anytime the auth object changes in state, we'll set whether the user is authenticated
  useEffect(() => {
    // set whether user is authenticated
    // setUserAuthenticated(auth.uid !== undefined);
    setUserAuthenticated(userIsAuthenticated);
  }, [auth]);


  const aggregation: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);
  const consolidation: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);
  const collaboration: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);
  const navigation: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);

  const digitalMedia: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);
  const filingCabinet: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);
  const fighterJet: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);
  const personalAndTeamUse: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);
  const worldGlobe: React.RefObject<SVGSVGElement | null> = useRef<SVGSVGElement>(null);


  return (
    <>
      {/* <div className={classes.paddingAtopPage} /> */}
      <StyledBoxForPaddingAtopPage />
      <GenericPageContainer
        maxWidth="xl"
        showBackButton={false}
        pageTitle={PageAndViewTitleStringAssets.pageTitle_About}
        showEducationalVideosButton={true}
        videoCatalogId={EducationalVideosStringAssets.gettingStartedCatalogId}
      >
        <>
          {/* <div> */}
          <StyledBoxLogoBoundingBox justifyContent={'center'} justifyItems={'center'}>
            {/* <MyDigiBrainLogo ref={logoElement} className={classes.myDigiBrainLogo} /> */}
            <StyledSvgForMyDigiBrainLogo ref={logoElement}>

            </StyledSvgForMyDigiBrainLogo>
          </StyledBoxLogoBoundingBox>
          {/* </div> */}

          {/* If user isn't authenticated, display the Sign-in and Register buttons */}
          {!userAuthenticated &&
            <Grid container direction={'column'}>
              <Grid container justifyContent={'center'}>
                <StyledGridForLoginButtonContainer>
                  <StyledButtonForLoginAndRegisterButtons onClick={() => navigate('/signin')}           >
                    {ControlsStringAssets.loginButtonText}
                  </StyledButtonForLoginAndRegisterButtons>
                </StyledGridForLoginButtonContainer>
                <StyledGridForCreateAccountButtonContainer>
                  <StyledButtonForLoginAndRegisterButtons onClick={() => navigate('/createNewAccount')}           >
                    {ControlsStringAssets.createNewAccountButtonText}
                  </StyledButtonForLoginAndRegisterButtons>
                </StyledGridForCreateAccountButtonContainer>
              </Grid>
              <Grid justifyItems={'center'} alignItems={'center'}>
                (Note: At this time, only authorized Beta Testers will be allowed to create an account and sign in.)
              </Grid>

              <Grid>
                <Grid>
                  <StyledTypographyForInformationText>
                    <b>
                      If you are not yet familiar with using MyDigiBrain, please consider viewing the two videos below.
                      The first video provides a brief introduction and overview. The second video provides instructions
                      for getting started by creating a new user account.
                    </b>
                  </StyledTypographyForInformationText>
                </Grid>

                <Grid container size={{ xs: 12 }} justifyItems={'center'} justifyContent={'center'}>
                  {
                    overviewVideoItem &&
                    <StyledGridForSideBySideVideosDisplay container direction={'column'} size={{ xs: 12, sm: 8, md: 6 }} >
                      <EducationalVideosVideoGridViewItem productVideo={overviewVideoItem} />
                    </StyledGridForSideBySideVideosDisplay>
                  }
                  {
                    creatingAUserAccountVideoItem &&
                    <StyledGridForSideBySideVideosDisplay container direction={'column'} size={{ xs: 12, sm: 8, md: 6 }} >
                      <EducationalVideosVideoGridViewItem productVideo={creatingAUserAccountVideoItem} />
                    </StyledGridForSideBySideVideosDisplay>
                  }
                </Grid>
              </Grid>
            </Grid>
          }

          <Grid container direction={'column'} justifySelf={'flex-start'} justifyItems={'flex-start'} justifyContent={'flex-start'}>
            <Grid>
              <StyledTypographyForInformationText>
                If you're like most, you've undoubtedly used your smart phone to take photos and videos, forms of digital media,
                that are stored on your device. And you've probably also received digital media from others via text or email
                messages and maybe shared on a web site or service such as Facebook, Instagram, YouTube, and others.
                Scattered across numerous devices and services, there's been no effective way to consolidate and aggregate your
                desired digital media in a way that suits your needs - until now!
              </StyledTypographyForInformationText>
            </Grid>

            {
              // if there is a valid educational video item to display, proceed to display it
              userAuthenticated && videoItemToDisplay &&
              <StyledGridForPrimaryVideoDisplay>
                <EducationalVideosVideoGridViewItem productVideo={videoItemToDisplay} />
              </StyledGridForPrimaryVideoDisplay>
            }

            <Grid>
              <StyledTypographyForInformationText>
                Introducing <b>MyDigiBrain</b>, the ultimate <b>Consolidator</b> and <b>Aggregator</b> of <b>Digital Media</b> for individuals and groups!
                With MyDigiBrain, you can consolidate a broad set of digital media assets, including photos, videos, and
                audio from your devices (smart phones, tablets, laptops, etc.). Further, MyDigiBrain allows you to aggregate other
                digital media assets, such as photos and images, videos, and audio from the cloud, including social media posts.
                In addition, MyDigiBrain allows you to aggregate documents, notes, and hyperlinks, other forms of digital media.
              </StyledTypographyForInformationText>
            </Grid>

            <StyledGridForDetailsSection>
              <StyledGridForDetailsBlock>
                <StyledGridForDetailsGraphicsArea>
                  <StyledBoxForFeatureGraphicsBoundingBox>
                    <StyledSvgForConsolidation ref={consolidation} />
                  </StyledBoxForFeatureGraphicsBoundingBox>
                </StyledGridForDetailsGraphicsArea>
                <Grid container size={{ xs: 8 }} justifyItems={'stretch'} alignItems={'center'}>
                  <StyledTypographyForDetailsSectionGeneralContent>
                    <span style={{ fontSize: 'larger' }}><b>Consolidate. </b></span><b>Consolidate</b> digital media from your mobile devices and computers, presented as <b>attractive galleries</b> of <b>images</b>, <b>videos</b>, and <b>other types</b> of digital media, <b>organized</b> in a way that best suits <b>you</b>.
                  </StyledTypographyForDetailsSectionGeneralContent>
                </Grid>
              </StyledGridForDetailsBlock>
              <StyledGridForDetailsBlock>
                <StyledGridForDetailsGraphicsArea>
                  <StyledBoxForFeatureGraphicsBoundingBox>
                    <StyledSvgForAggregation ref={aggregation} />
                  </StyledBoxForFeatureGraphicsBoundingBox>
                </StyledGridForDetailsGraphicsArea>
                <Grid container size={{ xs: 8 }} justifyItems={'stretch'} alignItems={'center'}>
                  <StyledTypographyForDetailsSectionGeneralContent>
                    <span style={{ fontSize: 'larger' }}><b>Aggregate. </b></span><b>Aggregate</b> and <b>integrate</b> other digital media from <b>social media</b> sites, <b>websites</b>, and <b>other services</b>, such <b>Facebook</b>, <b>Instagram</b>, <b>Twitter/X</b>, and <b>YouTube</b>, among others.
                  </StyledTypographyForDetailsSectionGeneralContent>
                </Grid>
              </StyledGridForDetailsBlock>
              <StyledGridForDetailsBlock>
                <StyledGridForDetailsGraphicsArea>
                  <StyledBoxForFeatureGraphicsBoundingBox>
                    <StyledSvgForCollaboration ref={collaboration} />
                  </StyledBoxForFeatureGraphicsBoundingBox>
                </StyledGridForDetailsGraphicsArea>
                <Grid container size={{ xs: 8 }} justifyItems={'stretch'} alignItems={'center'}>
                  <StyledTypographyForDetailsSectionGeneralContent>
                    <span style={{ fontSize: 'larger' }}><b>Collaborate. </b></span><b>Collaborate</b>, sharing your digital media with <b>others</b> and having others share with <b>you</b>.
                  </StyledTypographyForDetailsSectionGeneralContent>
                </Grid>
              </StyledGridForDetailsBlock>
              <StyledGridForDetailsBlock>
                <StyledGridForDetailsGraphicsArea>
                  <StyledBoxForFeatureGraphicsBoundingBox>
                    <StyledSvgForNavigation ref={navigation} />
                  </StyledBoxForFeatureGraphicsBoundingBox>
                </StyledGridForDetailsGraphicsArea>
                <Grid container size={{ xs: 8 }} justifyItems={'stretch'} alignItems={'center'}>
                  <StyledTypographyForDetailsSectionGeneralContent>
                    <span style={{ fontSize: 'larger' }}><b>Navigate. </b></span><b>Navigate</b> your digital media with <b>superior search</b> capabilities.
                  </StyledTypographyForDetailsSectionGeneralContent>
                </Grid>
              </StyledGridForDetailsBlock>
            </StyledGridForDetailsSection>

            <StyledTypographyForInformationText>
              <b>MyDigiBrain</b> may be used as a key <b>resource</b> or as a <b>collaborative portal</b> in <b>many</b> ways:
              <ul>
                <li><b>Managing</b> your <b>personal</b> digital media</li>
                <li><b>Weddings, Memorials, Graduations</b></li>
                <li><b>Travel</b></li>
                <li><b>Family</b> and <b>friend</b> events</li>
                <li><b>Team</b> activities</li>
                <li><b>Councils, Committees</b></li>
                <li><b>Coaching</b> and <b>Personal Training</b></li>
                <li><b>Sporting</b> and <b>entertainment</b> events</li>
                <li><b>Musical, dancing,</b> and <b>sports lessons</b></li>
                <li><b>Tournaments</b></li>
                <li>…and <b>many</b> more!</li>
              </ul>
            </StyledTypographyForInformationText>

            <StyledTypographyForInformationText>
              Get started with MyDigiBrain today!
            </StyledTypographyForInformationText>
          </Grid>
        </>
      </GenericPageContainer>
    </>
  );

}

export default AboutPage;
